import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  Avatar,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { GET_PERSONS, GET_PERSON, GET_TREE_DATA } from '../apollo/queries';
import { ADD_RELATIONSHIP } from '../apollo/mutations';
import { getDisplayName, getInitials } from '../hooks/usePersonHelpers';

function AddRelationshipDialog({ open, onClose, personId, type: initialType }) {
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [relationshipType, setRelationshipType] = useState(initialType || 'PARENT_CHILD');

  const { data: personsData } = useQuery(GET_PERSONS);

  const [addRelationship, { loading, error }] = useMutation(ADD_RELATIONSHIP, {
    refetchQueries: [
      { query: GET_PERSON, variables: { id: personId } },
      { query: GET_TREE_DATA },
    ],
    onCompleted: () => {
      handleClose();
    },
  });

  React.useEffect(() => {
    if (initialType) {
      setRelationshipType(initialType);
    }
  }, [initialType]);

  const handleClose = () => {
    setSelectedPerson(null);
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedPerson) return;

    // For PARENT_CHILD, the current person is the parent, selected person is child
    // For SPOUSE/PARTNER, order doesn't matter as much
    addRelationship({
      variables: {
        fromPersonId: personId,
        toPersonId: selectedPerson.id,
        type: relationshipType,
      },
    });
  };

  const availablePersons = personsData?.persons?.filter((p) => p.id !== personId) || [];

  const getRelationshipLabel = () => {
    switch (relationshipType) {
      case 'PARENT_CHILD':
        return 'Select Child';
      case 'SPOUSE':
        return 'Select Spouse';
      case 'PARTNER':
        return 'Select Partner';
      default:
        return 'Select Person';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Relationship</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}

        <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
          <InputLabel>Relationship Type</InputLabel>
          <Select
            value={relationshipType}
            label="Relationship Type"
            onChange={(e) => setRelationshipType(e.target.value)}
          >
            <MenuItem value="PARENT_CHILD">Parent → Child</MenuItem>
            <MenuItem value="SPOUSE">Spouse</MenuItem>
            <MenuItem value="PARTNER">Partner</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {relationshipType === 'PARENT_CHILD'
            ? 'The current person will be set as the parent of the selected person.'
            : 'This will create a spouse/partner relationship between the two people.'}
        </Typography>

        <Autocomplete
          options={availablePersons}
          getOptionLabel={(option) => getDisplayName(option)}
          value={selectedPerson}
          onChange={(_, value) => setSelectedPerson(value)}
          renderInput={(params) => (
            <TextField {...params} label={getRelationshipLabel()} />
          )}
          renderOption={(props, person) => (
            <Box component="li" {...props} key={person.id}>
              <Avatar
                src={person.profilePictureUrl}
                sx={{ mr: 2, width: 32, height: 32 }}
              >
                {getInitials(person)}
              </Avatar>
              {getDisplayName(person)}
            </Box>
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedPerson}
        >
          {loading ? <CircularProgress size={20} /> : 'Add Relationship'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddRelationshipDialog;

